import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { TestAccount, UserProfile } from '../../api/users.interface';
import {
    buildTestAccountLabel,
    buildTestAccountStateLabel,
} from './testAccounts.utils';

interface Props {
    visible: boolean;
    sourceUser: UserProfile;
    testAccounts: TestAccount[];
    submitting?: boolean;
    onCancel: () => void;
    onConfirm: (targetUid: string) => Promise<boolean>;
}

const CloneUserDialog: React.FC<Props> = ({
    visible,
    sourceUser,
    testAccounts,
    submitting,
    onCancel,
    onConfirm,
}) => {
    const [targetUid, setTargetUid] = React.useState('');
    const [confirming, setConfirming] = React.useState(false);

    React.useEffect(() => {
        if (!visible) {
            setTargetUid('');
            setConfirming(false);
        }
    }, [visible]);

    const selectedTarget = testAccounts.find(
        (testAccount) => testAccount.uid === targetUid,
    );

    const handleConfirm = async () => {
        if (!targetUid) {
            return;
        }

        const success = await onConfirm(targetUid);
        if (success) {
            onCancel();
        }
    };

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={visible && !confirming}
                onClose={onCancel}
            >
                <DialogTitle>Clone User</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Select the designated test account that should receive a full
                            copy of this data.
                        </Typography>
                        <TextField
                            select
                            label="Test Account"
                            value={targetUid}
                            onChange={(event) => setTargetUid(event.target.value)}
                            fullWidth
                            size="small"
                        >
                            {testAccounts.map((testAccount) => (
                                <MenuItem key={testAccount.uid} value={testAccount.uid}>
                                    {buildTestAccountLabel(testAccount)} • {buildTestAccountStateLabel(testAccount)}
                                </MenuItem>
                            ))}
                        </TextField>
                        {selectedTarget && (
                            <Typography variant="body2" color="textSecondary">
                                {selectedTarget.currentClonedProfile
                                    ? `Currently cloned from ${buildTestAccountLabel(selectedTarget.currentClonedProfile)}.`
                                    : ''}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onCancel} variant="outlined">Cancel</Button>
                    <Button
                        onClick={() => setConfirming(true)}
                        variant="contained"
                        disabled={!targetUid}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={visible && confirming}
                onClose={onCancel}
            >
                <DialogTitle>Confirm Clone</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <Typography variant="body1">
                            Clone{' '}
                            <strong>
                                {sourceUser.username || sourceUser.email || sourceUser.uid}
                            </strong>{' '}
                            into{' '}
                            <strong>
                                {selectedTarget
                                    ? buildTestAccountLabel(selectedTarget)
                                    : targetUid}
                            </strong>
                            .
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            This will replace the current data in the target test account with a full copy of the source user data.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setConfirming(false)} variant="outlined">
                        Back
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" disabled={submitting}>
                        {submitting ? 'Cloning...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CloneUserDialog;
